import { isEmptyVal } from '@/utils';
import _, { cloneDeep, find, get, groupBy, includes, map, pick, reduce } from 'lodash';
import { translation } from '@/plugins/i18n';
import {
  BASE_MEETING_PLAN_TYPES,
  BASE_PLAN_SUB_TYPES,
  BASE_PLAN_TYPES, BILLING_CYCLE,
  planTypes,
  ZOOM_EDUCATION_INDIVIDUAL_PLAN_TYPES,
  ZOOM_EDUCATION_OPTIONAL_PLAN_TYPES,
  zoomOneEnterpriseCategory,
} from '@/common/constants';
import { addToCartByCategoryAndBillingCycle } from '@/common/service/flow/utils';
import { getPlanTypeName } from '@/common/service/parse/parse-product-name';
import { pageConfig } from './service/page-config';
import { getPlanTypeByCategory } from './service/parse';

export const getPlanSelectedNumbers = (plan = {}) => {
  if (plan?.type === planTypes.TOLLFREE && plan?.category === planTypes.TOLLFREE) {
    return get(plan, 'cartInfo.tollfree_apac_hosts', 0);
  }

  return get(plan, 'cartInfo.selected_number', 0);
};

export const groupByPlanTypeGetter = (selectedPlans) => {
  const map = {};
  const _map = pick(groupBy(selectedPlans, (v) => v.type), Object.values(planTypes));
  Object.keys(_map).sort((k1, k2) => {
    const index1 = BASE_PLAN_TYPES.indexOf(k1);
    const index2 = BASE_PLAN_TYPES.indexOf(k2);
    if (index1 === -1) {
      return 1;
    }
    if (index2 === -1) {
      return -1;
    }
    return index1 - index2;
  }).forEach(k => {
    map[k] = _map[k];
  });
  return map;
};

export const sortByPlanList = (planList = []) => {
  return planList.sort((prev, next) => {
    const index1 = BASE_PLAN_TYPES.indexOf(prev?.type);
    const index2 = BASE_PLAN_TYPES.indexOf(next?.type);

    if (index1 === -1) {
      return 1;
    }

    if (index2 === -1) {
      return -1;
    }
    return index1 - index2;
  })
}

export const getOnlinePlanName = (plan, showFullName = false) => {
  if (!plan) return '';

  const planType = get(plan, 'type');
  const plan_name = get(plan, 'planNameKey');
  const capacity = get(plan, 'capacity');
  const typeName = get(plan, 'typeNameKey');
  let prefix = translation(typeName);

  if (planTypes.WEBINAR === planType) {
    if (getPlanTypeByCategory(plan?.category) === planTypes.WEBINAR_PROMO) {
      return isEmptyVal(capacity) ? prefix : `${prefix} ${capacity - 1}`;
    }

    return !capacity ? prefix : `${prefix} ${capacity}`;
  }

  if ([
    planTypes.LARGE_MEETING,
    planTypes.WEBINAR,
    planTypes.ZOOM_EVENTS,
    planTypes.EVENT_SAAS,
    planTypes.EVENT_SAAS_CORE,
  ].includes(planType)) {
    return !capacity ? prefix : `${prefix} ${capacity}`;
  }

  if (planType === planTypes.CMR) {
    const { cartCapacity, cartCapacityUnit } = plan || {};
    return isEmptyVal(cartCapacity) ? prefix : `${prefix} ${cartCapacity}${cartCapacityUnit}`;
  }

  if (planType === planTypes.WEBINAR_PROMO) {
    return isEmptyVal(capacity) ? prefix : `${prefix} ${capacity - 1}`;
  }

  if (planType === planTypes.ZOOM_MEETING) {
    return translation(plan_name) || prefix;
  }

  if ([planTypes.ZOOM_ONE_BUNDLE, planTypes.ZONE_PRO_PLUS, planTypes.ZONE_EDU_PLUS].includes(planType)) {
    const prefixPlanName = translation(plan_name) || prefix;

    if (!showFullName) return prefixPlanName;

    const pbxCategory = get(plan, "pbxCategory");
    const pbxCategoryName = pbxCategory ? translation(`billing.pbx.cart_name.${pbxCategory}`) : ''

    return `${prefixPlanName} ${pbxCategoryName}`;
  }

  // For pbx plan, need get this key to get planCartName, consistent with shopping cart
  if (planTypes.ZOOM_PHONE === planType) {
    const prefix = translation(typeName);
    return prefix + ' ' + translation(get(plan, "planCartNameKey"));
  }

  if ([planTypes.ADDITIONAL_CO_EDITOR, planTypes.ADDITIONAL_HOST].includes(planType)) {
    const basePlanType = _.chain(plan.category).split("-").get([0]).value();
    prefix = translation(getPlanTypeName(basePlanType));
    const planName = translation(getPlanTypeName(planType));
    return `${prefix} ${planName}`;
  }

  if ([planTypes.ZOOM_UNITED].includes(planType)) {
    const planName = plan_name ? `& ${translation(plan_name)}` : '';
    return `${prefix} ${planName}`;
  }

  return translation(plan_name) || prefix;
};

export const generatePlanList = (getters) => {
  const { getSelectedPlans = [] } = getters;

  return map(getSelectedPlans, (plan) => {
    return {
      planCode: plan.plan_code,
      quantity: get(plan, 'cartInfo.quantity', 0),
    };
  });
};

export function getPlansMap(plans = [], key = 'plan_code') {
  if (!plans.length) {
    return null;
  }

  return reduce(plans, (pre, item) => {
    const fieldKey = item[key];
    return { ...pre, [fieldKey]: cloneDeep(item) };
  }, {});
}

export const getBasePlansFrom = (plans = []) => {
  let basePlans = [];
  BASE_PLAN_TYPES.some((type) => {
    basePlans = plans.filter((item) => item.type === type);
    return !!basePlans.length;
  });
  return basePlans;
}

export const getRecommendPlansFrom = (plans = []) => {
  let basePlans = [];
  if (BASE_PLAN_TYPES.includes(plans[0]?.type)) {
    return [plans[0]];
  }
  BASE_PLAN_TYPES.some((type) => {
    basePlans = plans.filter((item) => item.type === type);
    return !!basePlans.length;
  });
  return basePlans;
}

export const getBasePlanInfo = (plans = []) => {
  let basePlans = [];
  const type = BASE_PLAN_TYPES.find(type => {
    basePlans = plans?.filter(item => item.type === type);
    return !!basePlans.length;
  });

  return {
    planType: type,
    plans: basePlans,
  };
}

export const getMeetingPlans = (plans = []) => {
  return plans?.filter(item => BASE_MEETING_PLAN_TYPES.includes(item.type));
}

// exclude capacities in enterprise plans
export const getNeedFilterCapacityList = (basePlans = []) => {
  const enterpriseType = zoomOneEnterpriseCategory[get(basePlans, [0, 'category'], '')?.toUpperCase()] || '';
  if ([zoomOneEnterpriseCategory['ZOENT'], zoomOneEnterpriseCategory['ZOENTST']].includes(enterpriseType)) {
    return [500]
  } else if ([zoomOneEnterpriseCategory['ZOPENT'], zoomOneEnterpriseCategory['ENT_PLUS_SITE']].includes(enterpriseType)) {
    return [1000]
  }
  return []
}

// filter zoom meeting/zoom one bizplus/zoom united
export const filterBundleSeries = plan => (
  plan.type === planTypes.ZOOM_MEETING ||
  plan.type === planTypes.ZOOM_UNITED ||
  plan.type === planTypes.ZOOM_ONE_BUNDLE
);

export const addBasePlanByTargetAddonPlan = (planMapFlat, targetPlan = {}, basePlanType, billingCycle = BILLING_CYCLE.ANNUAL, defaultBaseType, license, defaultBaseSubCate) => {
  const { dependBasePlanTypeList = [] } = targetPlan;

  // * edu plan init handler
  // * add base plan meeting education into cart
  if (
    window.$context.is.edu
    && [
      ...ZOOM_EDUCATION_INDIVIDUAL_PLAN_TYPES,
      ...ZOOM_EDUCATION_OPTIONAL_PLAN_TYPES
    ].includes(targetPlan.type)
    && !includes(dependBasePlanTypeList, basePlanType)
  ) {
    const basePlan = find(planMapFlat, (p) => {
      return p.type === planTypes.ZOOM_MEETING && p.category === BASE_PLAN_SUB_TYPES.EDUCATION
    });
    // if the period is one_time or pay per attendee, need base billing cycle reset to annual by default
    if (![BILLING_CYCLE.MONTHLY, BILLING_CYCLE.ANNUAL].includes(billingCycle)) {
      billingCycle = BILLING_CYCLE.ANNUAL;
    }

    addToCartByCategoryAndBillingCycle(planMapFlat, basePlan.type, basePlan.category, billingCycle, license);
  } else if (!isEmptyVal(dependBasePlanTypeList) && !includes(dependBasePlanTypeList, basePlanType)) {
    // add base plan to shopping cart
    let baseType = dependBasePlanTypeList[0]
    if(dependBasePlanTypeList.includes(defaultBaseType)) {
      baseType = defaultBaseType;
    }
    let basePlan = find(planMapFlat, (p) => p.type === baseType);
    if (defaultBaseSubCate) {
      basePlan = find(planMapFlat, (p) => p.type === baseType && p.category === defaultBaseSubCate)
    }

    // if the period is one_time or pay per attendee, need base billing cycle reset to annual by default
    if (![BILLING_CYCLE.MONTHLY, BILLING_CYCLE.ANNUAL].includes(billingCycle)) {
      billingCycle = BILLING_CYCLE.ANNUAL;
    }

    addToCartByCategoryAndBillingCycle(planMapFlat, basePlan?.type || planTypes.ZOOM_MEETING, basePlan?.category || BASE_PLAN_SUB_TYPES.PRO, billingCycle, license);
  }

  return planMapFlat;
}

export const addDependBasePlan = (planMapFlat, targetPlan) => {
  const { dependBasePlanTypeList } = targetPlan;
  const plan = pageConfig.plan;
  const from = pageConfig.from;
  const typeOfPlan = getPlanTypeByCategory(plan);
  const typeOfFrom = getPlanTypeByCategory(from);
  const selectedPlans = planMapFlat.filter(item => item.cartInfo.selected_number > 0);
  const isTypeSelected = type => selectedPlans.find(item => item.type === type);

  if (isEmptyVal(dependBasePlanTypeList)) {
    // For standAlone
    if (
      typeOfFrom !== typeOfPlan &&
      typeOfPlan === planTypes.ZOOM_MEETING) {

      !isTypeSelected(typeOfPlan) && addToCartByCategoryAndBillingCycle(
        planMapFlat,
        typeOfPlan,
        plan,
        BILLING_CYCLE.ANNUAL,
      );
    }
    return planMapFlat;
  }
  if (!dependBasePlanTypeList.includes(typeOfPlan)) {
    const basePlan = find(planMapFlat, (p) => p.type === dependBasePlanTypeList[0]);

    !isTypeSelected(typeOfPlan) && addToCartByCategoryAndBillingCycle(
      planMapFlat,
      basePlan?.type || planTypes.ZOOM_MEETING,
      basePlan.category || BASE_PLAN_SUB_TYPES.PRO,
      BILLING_CYCLE.ANNUAL,
    );
  } else {
    const basePlan = find(planMapFlat, (p) => (
      p.type === typeOfPlan && p.category === plan
    ));

    if (!isTypeSelected(typeOfPlan)) {
      if (targetPlan.type === planTypes.WEBINAR && basePlan?.category === BASE_PLAN_SUB_TYPES.ZONE_EDU_SCH_CMP) {
        addWebinarOneTimePsoRelatedPlan(planMapFlat, targetPlan, false);
      }
      addToCartByCategoryAndBillingCycle(
        planMapFlat,
        basePlan?.type || planTypes.ZOOM_MEETING,
        basePlan.category || BASE_PLAN_SUB_TYPES.PRO,
        BILLING_CYCLE.ANNUAL,
      );
    }
  }
  return planMapFlat;
}


export const addBasePlanToStandAlone = (planMapFlat, targetPlan = {}, plan, billingCycle = BILLING_CYCLE.ANNUAL) => {
  const { standAlone = false } = targetPlan;
  if (standAlone &&  plan === BASE_PLAN_SUB_TYPES.PRO) {

    if (![BILLING_CYCLE.MONTHLY, BILLING_CYCLE.ANNUAL].includes(billingCycle)) {
      billingCycle = BILLING_CYCLE.ANNUAL;
    }

    addToCartByCategoryAndBillingCycle(planMapFlat, BASE_MEETING_PLAN_TYPES[0], BASE_PLAN_SUB_TYPES.PRO, billingCycle);
  }

  return planMapFlat;
}

export const addWebinarOneTimePsoRelatedPlan = (planMapFlat, targetPlan = {}, isNeedSingleUsePro = true) => {
  [
    planMapFlat.find(p => p.plan_code === targetPlan.esmd_plancode),
    isNeedSingleUsePro && planMapFlat.find(p => p.billing_cycle === BILLING_CYCLE.ONE_TIME && p.type === planTypes.ZOOM_MEETING)
  ].filter(p => p).forEach((p) => {
    addToCartByCategoryAndBillingCycle(planMapFlat, p.type, p.category, p.billing_cycle);
  });
  return planMapFlat;
}
